import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Spinner, AppLayout } from '@amzn/awsui-components-react-v3';
import { DataLineagePage,
  useApiClientStore,
  useLineageStore,
  useSplitPanelStore,
  getSecondaryPanelDrawers,
  SecondaryPanelId,
} from '@amzn/aws-fintech-data-lattice-ui-commons';
import { QueryClientProvider } from '@tanstack/react-query';
import { getMidwayCredentialProviderSTS} from "src/api/auth";
import {queryClient} from "src/components/lineagev2/commonV2";
import '@xyflow/react/dist/style.css';
import '@amzn/aws-fintech-data-lattice-ui-commons/dist/styles/index.scss';
import './lineageV2.css';
import {getLatticeEndpoint} from "src/api/latticelineage";


export interface LineageDetailV2Props {
  baseNodeId: string;
}

export const LineageDetailV2 = (props: LineageDetailV2Props) => {
  const updateCenterNode = useLineageStore((state) => state.updateCenterNode);
  const initialize = useApiClientStore((state) => state.initialize);
  const apiClient = useApiClientStore((state) => state.apiClient);
  const apiClientInitialized = apiClient?.isInitialized() ?? false;

  // Split panel store for handling side panels
  const splitPanelId = useSplitPanelStore((state) => state.splitPanelId);
  const splitPanelSize = useSplitPanelStore((state) => state.splitPanelSize);
  const onSplitPanelResize = useSplitPanelStore((state) => state.onSplitPanelResize)
  const renderSplitPanel = useSplitPanelStore((state) => state.renderSplitPanel);
  const onSplitPanelToggle = useSplitPanelStore((state) => state.onSplitPanelToggle)
  const splitPanelOpen = !!splitPanelId;

  const drawerId = useSplitPanelStore((state) => state.drawerId) ?? null;
  const drawerSize = useSplitPanelStore((state) => state.drawerSize);
  const setDrawerSize = useSplitPanelStore((state) => state.setDrawerSize);
  const onDrawerChange = useSplitPanelStore((state) => state.onDrawerChange);


  useEffect(() => {
    useSplitPanelStore.setState({ drawerId: undefined });
  }, [splitPanelId]);

  const midwayCredProvider = useMemo(() => getMidwayCredentialProviderSTS(), []);

  useEffect(() => {
    if (midwayCredProvider && !apiClientInitialized) {
      console.log(`initializing lattice client. current status:${apiClientInitialized}`)
      initialize(getLatticeEndpoint(), midwayCredProvider);
    }
  }, [midwayCredProvider, apiClientInitialized]);

  useEffect(() => {
    if (apiClientInitialized && props.baseNodeId) {
      // Set center node - this triggers useLineage hook
      updateCenterNode({
        id: props.baseNodeId,
        NodeType: 'Dataset'
      });
    }
  }, [apiClientInitialized, props.baseNodeId, updateCenterNode]);


  if (!apiClientInitialized || !props.baseNodeId) {
    return <Spinner size='large' />;
  }

  return (
    <div className="lineage-v2-container">
      <QueryClientProvider client={queryClient}>
        <AppLayout
          content={
            <DataLineagePage
              headerSplitPanel={SecondaryPanelId.ResourceDetails}
              showLineageLevelSelector={false}
              headerSplitPanelIcon='view-vertical'
              galaxiControlsConfiguration={false}
            />
          }
          splitPanel={renderSplitPanel()}
          splitPanelOpen={splitPanelOpen}
          onSplitPanelToggle={onSplitPanelToggle}
          splitPanelSize={splitPanelSize}
          onSplitPanelResize={onSplitPanelResize}
          splitPanelPreferences={{ position: 'side' }}
          drawers={Object.values(getSecondaryPanelDrawers(drawerSize, setDrawerSize))}
          activeDrawerId={drawerId}
          onDrawerChange={onDrawerChange}
          toolsHide={true}
          tools={undefined}
          navigationHide
        />
      </QueryClientProvider>
    </div>
  );

};
